import { customized_style } from 'shared/config/Styles';

/** Re-exported from shared so shipment-monitoring can consume them without importing this feature. */
export { StyledPopper, formLabelTypographySx } from 'shared/config/Styles';

/**
 * `Read.il_rate_management_shipment_search` 의 `rate_mgmt_tab` / `id`(`rm-{tab}-{pk}`) 과 동일한 출하 탭.
 * @typedef {0|1|2|3|4} RateManagementShipmentTab
 */

/** @type {Record<RateManagementShipmentTab, { rateKeyName: string, socketTab: number }>} */
const RATE_MANAGEMENT_RATE_CONTEXT_BY_SHIPMENT_TAB = {
    0: { rateKeyName: 'il_ocean_import_id', socketTab: 0 },
    1: { rateKeyName: 'il_ocean_export_id', socketTab: 1 },
    2: { rateKeyName: 'il_air_import_id', socketTab: 2 },
    3: { rateKeyName: 'il_air_export_id', socketTab: 3 },
    4: { rateKeyName: 'il_ftl_ltl_id', socketTab: 4 },
};

/**
 * 검색 행의 출하 탭(해상/항공/트럭킹). 백엔드 `rate_mgmt_tab` 우선, 없으면 `id`의 `rm-{tab}-…`에서 파싱.
 * @param {{ id?: unknown, rate_mgmt_tab?: unknown }} row
 * @returns {RateManagementShipmentTab}
 */
function getRateManagementShipmentTab(row) {
    const fromField = row?.rate_mgmt_tab;
    if (fromField != null && fromField !== '') {
        const t = Number(fromField);
        if (Number.isFinite(t) && t >= 0 && t <= 4) {
            return /** @type {RateManagementShipmentTab} */ (t);
        }
    }
    const rid = row?.id;
    if (typeof rid === 'string' && rid.startsWith('rm-')) {
        const parts = rid.split('-');
        if (parts.length >= 3) {
            const t = Number(parts[1]);
            if (Number.isFinite(t) && t >= 0 && t <= 4) {
                return /** @type {RateManagementShipmentTab} */ (t);
            }
        }
    }
    return 0;
}

/**
 * Rate Management 검색 행: 그리드 `id`가 `rm-{tab}-{부모PK}` 일 때 DB용 부모 정수 id.
 * `rm-` 형식을 숫자 `id`보다 우선해, 항공 등 다른 탭과 동일 숫자 PK가 섞여도 잘못된 FK를 쓰지 않게 한다.
 * @param {{ id?: unknown }} row
 * @returns {number | null}
 */
function getRateManagementParentDbId(row) {
    if (!row) return null;
    const rid = row.id;
    if (typeof rid === 'string' && rid.startsWith('rm-')) {
        const parts = rid.split('-');
        if (parts.length >= 3) {
            const n = Number(parts[parts.length - 1]);
            return Number.isFinite(n) ? n : null;
        }
    }
    if (typeof rid === 'number' && Number.isFinite(rid)) return rid;
    return null;
}

/**
 * 상세 패널 `il_rate` 소켓/Redux 에 필요한 출하 탭별 메타 + 부모 PK.
 * @param {object} row
 * @returns {{ shipmentTab: RateManagementShipmentTab, parentDbId: number, rateKeyName: string, socketTab: number } | null}
 */
export function getRateManagementRateContext(row) {
    const shipmentTab = getRateManagementShipmentTab(row);
    const meta = RATE_MANAGEMENT_RATE_CONTEXT_BY_SHIPMENT_TAB[shipmentTab];
    const parentDbId = getRateManagementParentDbId(row);
    if (!meta || parentDbId == null) return null;
    return { shipmentTab, parentDbId, ...meta };
}

/** Rate Management 메인/디테일 그리드 셀 Typography (11px). */
export const rateManagementCellTypographySx = {
    ...customized_style.itemDatagrid_Typography,
    fontSize: '11px',
};

/**
 * MainDatagrid 및 Detail panel 안쪽 중첩 그리드까지 11px.
 * MainDatagrid `sx`에 spread (height 등과 병합).
 */
export const rateManagementDataGridFontSx = {
    fontSize: '11px',
    '& .MuiDataGrid-columnHeaderTitle': { fontSize: '12px', lineHeight: 1.2 },
    '& .MuiDataGrid-columnHeaders': { fontSize: '12px' },
    '& .MuiDataGrid-columnHeader': { fontSize: '12px' },
    '& .MuiDataGrid-cell': { fontSize: '12px' },
    '& .MuiDataGrid-footerContainer': { fontSize: '12px' },
    '& .MuiDataGrid-toolbarContainer': { fontSize: '12px' },
    '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': { fontSize: '12px' },
    '& .MuiDataGrid-panelWrapper': { fontSize: '12px' },
    '& .MuiDataGrid-panel': { fontSize: '12px' },
    '& .MuiButtonBase-root': { fontSize: '12px' },
    '& .MuiInputBase-root': { fontSize: '12px' },
    '& .MuiInputBase-input': { fontSize: '12px' },
    '& .MuiDataGrid-cell .MuiTypography-root': { fontSize: '12px' },
    '& .MuiDataGrid-detailPanel': {
        overflow: 'visible',
        padding: 0,
        fontSize: '11px',
        /* MainDatagrid `sx`의 `& .MuiDataGrid-main` 이 후손 전체에 적용되어 중첩 NET/SELLING 그리드에도 radius가 붙음 → 디테일 안만 제거 */
        '& .MuiDataGrid-main': { borderRadius: 0, boxShadow: 'none' },
        '& .MuiTypography-root': { fontSize: '11px' },
        '& .MuiDataGrid-root': { fontSize: '11px' },
        '& .MuiDataGrid-columnHeaderTitle': { fontSize: '11px', lineHeight: 1.15 },
        '& .MuiDataGrid-columnHeader': { fontSize: '11px', py: 0 },
        '& .MuiDataGrid-cell': { fontSize: '11px', py: 0 },
        '& .MuiDataGrid-cell .MuiTypography-root': { fontSize: '11px' },
        '& .MuiButtonBase-root': { fontSize: '11px' },
        '& .MuiInputBase-root': { minHeight: 26, fontSize: '11px' },
        '& .MuiInputBase-input': { fontSize: '11px', py: 0.25 },
        '& .MuiSelect-select': { fontSize: '11px', py: 0.25 },
    },
};

export const formAutocompleteSx = {
    flex: 1,
    minWidth: 0,
    height: '27px',
    '& .MuiInputBase-input': { fontSize: '13px', height: '12px' },
};

/** Rate Management 상단 검색 필터 전용 — 라벨·입력 높이 축소, 한 줄 말줄임으로 행 높이 통일. */
export const rateManagementFilterLabelSx = {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    minHeight: 24,
    color: '#003a52',
    fontSize: '12px',
    fontWeight: 500,
    width: '110px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

export const rateManagementFilterAutocompleteSx = {
    flex: 1,
    minWidth: 0,
    maxWidth: 'none',
    height: '24px',
    '& .MuiInputBase-root': {
        minHeight: 24,
        height: 24,
        fontSize: '12px',
        py: 0,
    },
    '& .MuiInputBase-input': { fontSize: '12px', height: '18px', py: 0 },
};

function collectQuickSearchParts(value, parts, depth = 0) {
    if (value == null || depth > 6) return;
    if (typeof value === 'string') {
        const s = value.trim();
        if (s) parts.push(s);
        return;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        parts.push(String(value));
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item) => collectQuickSearchParts(item, parts, depth + 1));
        return;
    }
    if (typeof value === 'object') {
        for (const v of Object.values(value)) {
            collectQuickSearchParts(v, parts, depth + 1);
        }
    }
}

/**
 * 검색 결과 행(중첩 객체·배열 포함)의 모든 값을 재귀적으로 평탄화해 `_quickSearch_` 문자열로 붙인다.
 * `MainDatagrid`의 QuickFilter가 `_quickSearch_` 숨김 컬럼을 통해 이 문자열을 검색하도록 한다.
 */
export function attachRateManagementQuickSearchField(row) {
    const parts = [];
    for (const [key, value] of Object.entries(row)) {
        if (key === '_quickSearch_') continue;
        collectQuickSearchParts(value, parts);
    }
    return { ...row, _quickSearch_: parts.join(' ') };
}
